import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api } from '@/api/client'
import type { Space } from '@/types/models'
import { useCalendarStore } from './calendarStore'
import { useTagsStore } from './tagsStore'
import { useEventStore } from './eventStore'
import { useDiaryStore } from './diaryStore'
import { usePeriodStore } from './periodStore'

interface SpaceState {
  activeSpace: string
  lastMainSpace: string
  spaces: Space[]
  currentSpace: Space | null
  loadSpaces: () => Promise<void>
  switchSpace: (space: string) => void
  switchToLastMain: () => void
  createSpace: (name: string, type: 'couple' | 'team', icon?: string, periodEnabled?: boolean) => Promise<Space | undefined>
  joinSpace: (inviteCode: string) => Promise<void>
  leaveSpace: (spaceId: string) => Promise<void>
  deleteSpace: (spaceId: string) => Promise<void>
  loadSpaceDetail: (spaceId: string) => Promise<Space>
  removeMember: (spaceId: string, uid: string) => Promise<Space>
  transferAdmin: (spaceId: string, uid: string) => Promise<Space>
  updateSettings: (spaceId: string, data: { spaceName?: string; type?: string; icon?: string; periodEnabled?: boolean; periodVisible?: boolean; periodEditable?: boolean; periodSync?: boolean; safePeriodEnabled?: boolean }) => Promise<void>
}

export const useSpaceStore = create<SpaceState>()(
  persist(
    (set, get) => ({
      activeSpace: 'default',
      lastMainSpace: 'default',
      spaces: [],
      currentSpace: null,

      loadSpaces: async () => {
        const spacesData = await api.get<Space[]>('/api/spaces')
        const spaces = Array.isArray(spacesData) ? spacesData : []
        const state = get()
        let currentSpace = null
        if (state.activeSpace.startsWith('space:')) {
          const sid = state.activeSpace.replace('space:', '')
          currentSpace = spaces.find(s => s.id === sid) || null
        } else {
          // 非空间模式时确保 currentSpace 为 null
          currentSpace = null
        }
        set({ spaces, currentSpace })
      },

      switchSpace: (space) => {
        const spaces = get().spaces
        let currentSpace = null
        if (space.startsWith('space:')) {
          const sid = space.replace('space:', '')
          currentSpace = spaces.find(s => s.id === sid) || null
        }
        const update: Partial<SpaceState> = { activeSpace: space, currentSpace: currentSpace as Space | null }
        // vault 和 period 是特殊空间，不更新 lastMainSpace
        if (space !== 'vault' && space !== 'period') {
          update.lastMainSpace = space
        }
        set(update as SpaceState)
        // 清空所有缓存数据，避免切换时显示旧空间内容
        useCalendarStore.setState({ monthActs: {}, loadingMonth: false, error: null })
        useTagsStore.setState({ tags: [], loaded: false })
        useEventStore.setState({ events: [], loaded: false })
        usePeriodStore.setState({ records: [], spacePeriodRecords: [] })
        useDiaryStore.getState().clearDiary()
        // 切换到空间后异步刷新成员数据（昵称/头像可能已更新）
        if (space.startsWith('space:')) {
          const sid = space.replace('space:', '')
          get().loadSpaceDetail(sid).then(detail => {
            // 只有当前空间仍然是目标空间时才更新 currentSpace
            if (get().activeSpace === space && detail) {
              set({ currentSpace: detail as Space })
            }
          })
        }
      },

      switchToLastMain: () => {
        const { lastMainSpace, spaces } = get()
        // 防御：如果 lastMainSpace 是特殊空间，回退到 default
        const target = (lastMainSpace === 'vault' || lastMainSpace === 'period') ? 'default' : lastMainSpace
        let currentSpace = null
        if (target.startsWith('space:')) {
          const sid = target.replace('space:', '')
          currentSpace = spaces.find(s => s.id === sid) || null
        }
        set({ activeSpace: target, currentSpace: currentSpace as Space | null })
        useCalendarStore.setState({ monthActs: {}, loadingMonth: false, error: null })
      },

      createSpace: async (name, type, icon, periodEnabled) => {
        const res = await api.post<{ ok: boolean; space: Space }>('/api/spaces', { name, type, icon, periodEnabled })
        await get().loadSpaces()
        if (res.space) {
          get().switchSpace('space:' + res.space.id)
        }
        return res.space
      },

      joinSpace: async (inviteCode) => {
        await api.post('/api/spaces/join', { inviteCode })
        await get().loadSpaces()
      },

      leaveSpace: async (spaceId) => {
        await api.delete(`/api/spaces/${spaceId}/leave`)
        const state = get()
        if (state.activeSpace === `space:${spaceId}`) {
          set({ activeSpace: 'default', currentSpace: null })
        }
        await get().loadSpaces()
      },

      deleteSpace: async (spaceId) => {
        await api.delete(`/api/spaces/${spaceId}`)
        const state = get()
        if (state.activeSpace === `space:${spaceId}`) {
          set({ activeSpace: 'default', currentSpace: null })
        }
        await get().loadSpaces()
      },

      loadSpaceDetail: async (spaceId) => {
        const space = await api.get<Space>(`/api/spaces/${spaceId}`)
        set({ currentSpace: space })
        return space
      },

      removeMember: async (spaceId, uid) => {
        const res = await api.delete<{ ok: boolean; space: Space }>(`/api/spaces/${spaceId}/members/${uid}`)
        set({ currentSpace: res.space })
        return res.space
      },

      transferAdmin: async (spaceId, uid) => {
        const res = await api.post<{ ok: boolean; space: Space }>(`/api/spaces/${spaceId}/transfer`, { targetUid: uid })
        set({ currentSpace: res.space })
        return res.space
      },

      updateSettings: async (spaceId, data) => {
        const res = await api.put<{ ok: boolean; space?: Space; settings?: Record<string, unknown> }>(`/api/spaces/${spaceId}/settings`, data)
        if (res.space) {
          set(s => ({
            currentSpace: res.space,
            spaces: s.spaces.map(sp => sp.id === spaceId ? res.space! : sp),
          }))
          if (res.space.settings?.periodVisible !== undefined) {
            usePeriodStore.getState().setVisible(res.space.settings.periodVisible)
          }
        }
      },
    }),
    {
      name: 'cal_space',
      partialize: (state) => ({ activeSpace: state.activeSpace }),
    }
  )
)
